import { createClient } from './http';

const MAPS_API_BASE = import.meta.env.VITE_MAPS_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/maps';

const request = createClient(MAPS_API_BASE);

/** Data types a dictionary's OWN fields can have — deliberately excludes
 * 'dictionary' itself (no nested/chained references between dictionaries). */
export type DictionaryFieldDataType = 'string' | 'text' | 'integer' | 'decimal' | 'boolean' | 'date' | 'datetime';

export type DictionaryFieldDetail = {
  code: string;
  name: string;
  data_type: DictionaryFieldDataType;
  required: boolean;
};

export type DictionaryDto = {
  id: string;
  code: string;
  name: string;
  value_count: number;
  updated_at: string;
};

export type DictionaryDetail = DictionaryDto & {
  fields: DictionaryFieldDetail[];
};

export type DictionaryValue = {
  id: string;
  name: string;
  properties: Record<string, unknown>;
  sort_order: number;
  photo_file_id: string | null;
};

export type DictionaryCreateInput = {
  name: string;
  code: string;
  fields: DictionaryFieldDetail[];
};

export const dictionariesApi = {
  listDictionaries: async (): Promise<DictionaryDto[]> => request<DictionaryDto[]>('/dictionaries', {}, true),

  createDictionary: async (body: DictionaryCreateInput): Promise<DictionaryDetail> =>
    request<DictionaryDetail>('/dictionaries', { method: 'POST', body: JSON.stringify(body) }, true),

  getDictionary: async (dictionaryId: string): Promise<DictionaryDetail> =>
    request<DictionaryDetail>(`/dictionaries/${dictionaryId}`, {}, true),

  updateDictionary: async (dictionaryId: string, body: DictionaryCreateInput): Promise<DictionaryDetail> =>
    request<DictionaryDetail>(`/dictionaries/${dictionaryId}`, { method: 'PATCH', body: JSON.stringify(body) }, true),

  deleteDictionary: async (dictionaryId: string): Promise<void> =>
    request<void>(`/dictionaries/${dictionaryId}`, { method: 'DELETE' }, true),

  listValues: async (dictionaryId: string): Promise<DictionaryValue[]> =>
    request<DictionaryValue[]>(`/dictionaries/${dictionaryId}/values`, {}, true),

  createValue: async (
    dictionaryId: string,
    name: string,
    properties: Record<string, unknown> = {},
  ): Promise<DictionaryValue> =>
    request<DictionaryValue>(
      `/dictionaries/${dictionaryId}/values`,
      { method: 'POST', body: JSON.stringify({ name, properties }) },
      true,
    ),

  getValue: async (dictionaryId: string, valueId: string): Promise<DictionaryValue> =>
    request<DictionaryValue>(`/dictionaries/${dictionaryId}/values/${valueId}`, {}, true),

  updateValue: async (
    dictionaryId: string,
    valueId: string,
    name: string,
    properties: Record<string, unknown> = {},
  ): Promise<DictionaryValue> =>
    request<DictionaryValue>(
      `/dictionaries/${dictionaryId}/values/${valueId}`,
      { method: 'PATCH', body: JSON.stringify({ name, properties }) },
      true,
    ),

  deleteValue: async (dictionaryId: string, valueId: string): Promise<void> =>
    request<void>(`/dictionaries/${dictionaryId}/values/${valueId}`, { method: 'DELETE' }, true),

  /** Sets (or clears, with fileId=null) a dictionary value's single photo — a
   * fixed attribute of the value itself, not part of the dictionary's field schema. */
  setValuePhoto: async (dictionaryId: string, valueId: string, fileId: string | null): Promise<DictionaryValue> =>
    request<DictionaryValue>(
      `/dictionaries/${dictionaryId}/values/${valueId}/photo`,
      { method: 'PATCH', body: JSON.stringify({ file_id: fileId }) },
      true,
    ),
};
